/**
 * IssuesRail — the Production Issues page's left navigation.
 *
 * Mirrors the Production Settings Console rail (`SettingsRail`): a second-level
 * nav INSIDE the content area, one item per rendered issue type, active item
 * gets a brand-purple left border + card fill. On xl+ it is a vertical rail
 * that the PAGE keeps sticky so it never scrolls away. Below xl it sits above
 * the pane and WRAPS onto as many rows as it needs (never a sideways scroll
 * strip), so every issue type and its count is on screen at once.
 *
 * Differences from SettingsRail, on purpose:
 * - The count pill shows at EVERY viewport size (and only when > 0): on the
 *   Issues page the counts are the point, phones included.
 * - Wraps below xl instead of scrolling sideways, for the same reason.
 * - Items are 48px tall below xl (touch / gloved hands), 40px on the desk rail.
 * - No search entry, no footer.
 * - Items stay real `<button>`s inside a `<ul>/<li>` list (no `role` override),
 *   so assistive tech announces them as buttons.
 *
 * The tab descriptors, visibility rules and badge counts all come from
 * `../issueTabs`; this component only renders what it is given.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { IssueTabDescriptor } from '../issueTabs';
import type { TabValue } from '../types';

interface IssuesRailProps {
  /** Rendered tabs, already filtered by the page (hidden / client / CX rules). */
  tabs: readonly IssueTabDescriptor[];
  activeTab: TabValue;
  onSelect: (value: TabValue) => void;
  /** Badge count for a tab; the pill is hidden when this returns 0. */
  badgeFor: (value: TabValue) => number;
}

export function IssuesRail({ tabs, activeTab, onSelect, badgeFor }: IssuesRailProps) {
  const { t } = useTranslation('production');

  return (
    <nav aria-label={t('issues.rail.aria')} className="xl:w-60 xl:shrink-0">
      <ul
        // p-1 at every size gives the active item's ring + shadow room inside
        // the page's sticky wrapper (xl:overflow-y-auto), which would
        // otherwise clip them.
        className="flex flex-wrap gap-1 p-1 xl:flex-col xl:flex-nowrap xl:gap-0.5"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.value === activeTab;
          const count = badgeFor(tab.value);
          return (
            <li key={tab.value} className="min-w-0 max-w-full">
              <button
                type="button"
                onClick={() => onSelect(tab.value)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'group flex min-h-12 items-center gap-2.5 rounded-md border-l-2 border-transparent px-2.5 py-2.5 text-left text-sm transition-colors motion-reduce:transition-none',
                  // Inset the global focus outline: the page's sticky wrapper
                  // (xl:overflow-y-auto) would otherwise clip an
                  // outward-offset ring.
                  'focus-visible:outline-offset-[-2px]',
                  'xl:min-h-10 xl:w-full',
                  active
                    ? 'border-l-threadheads-purple bg-card text-foreground shadow-sm ring-1 ring-border/60'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <Icon
                  className={cn('h-4 w-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate font-medium">{t(tab.labelKey)}</span>
                {count > 0 && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-xs font-semibold tabular-nums',
                      // text-primary-strong on the brand tint: plain
                      // text-primary is 3.9:1 there and fails AA (DESIGN.md §1).
                      active ? 'bg-primary/10 text-primary-strong' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
